import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

const PatternContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
  background-color: #f9f9f9;
  padding: 2rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  flex-wrap: wrap;
`;

const PatternItem = styled.div<{ color?: string }>`
  width: 80px;
  height: 80px;
  margin: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  background-color: ${props => props.color || 'var(--secondary-color)'};
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  transition: all 0.3s ease;

  &.empty {
    background-color: rgba(0, 0, 0, 0.1);
    border: 3px dashed #ccc;
    box-shadow: none;
  }

  &.selectable {
    cursor: pointer;

    &:hover {
      transform: scale(1.05);
    }
  }

  &.selected {
    border: 3px solid var(--primary-color);
  }

  &.correct {
    background-color: var(--success-color);
    color: white;
  }

  &.incorrect {
    background-color: var(--error-color);
    color: white;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 2rem 0;
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }
`;

const InstructionText = styled.p`
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

interface PatternType {
  type: 'numbers' | 'shapes';
  items: Array<string | number>;
  missingIndex: number;
  options: Array<string | number>;
  answer: string | number;
}

type PatternGeneratorFunction = () => PatternType;

const generateNumberPattern = (): PatternType => {
  // For nearly 5-year-olds, we'll use simple patterns like counting by 2s, 5s, or simple sequences
  const patternTypes = [
    { step: 1, start: 1, length: 5 },  // 1, 2, 3, 4, 5
    { step: 2, start: 2, length: 5 },  // 2, 4, 6, 8, 10
    { step: 5, start: 5, length: 5 },  // 5, 10, 15, 20, 25
    { step: 10, start: 10, length: 5 } // 10, 20, 30, 40, 50
  ];

  const selectedPattern = patternTypes[Math.floor(Math.random() * patternTypes.length)];
  const { step, start, length } = selectedPattern;

  const items: number[] = [];
  for (let i = 0; i < length; i++) {
    items.push(start + (step * i));
  }

  // Choose a random position to be the missing item (but not the first position for clarity)
  const missingIndex = Math.floor(Math.random() * (length - 1)) + 1;
  const answer = items[missingIndex];

  // Generate options including the correct answer
  const options = [answer];

  // Add some incorrect but plausible options
  while (options.length < 4) {
    // For incorrect options, we'll use values close to the correct answer
    // but that don't follow the pattern
    const offset = Math.floor(Math.random() * 5) + 1;
    const incorrectOption = answer + (Math.random() > 0.5 ? offset : -offset);

    if (incorrectOption > 0 && !options.includes(incorrectOption) && !items.includes(incorrectOption)) {
      options.push(incorrectOption);
    }
  }

  // Shuffle options
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }

  return {
    type: 'numbers',
    items,
    missingIndex,
    options,
    answer
  };
};

const generateShapePattern = (): PatternType => {
  // For shapes, we'll use emoji as a simple representation
  const shapes = ['🔴', '🔵', '🟢', '🟡', '🟣', '⚫', '⭐', '🔶'];
  const patternOptions = [
    // Alternating pattern
    () => {
      const shape1 = shapes[Math.floor(Math.random() * shapes.length)];
      let shape2;
      do {
        shape2 = shapes[Math.floor(Math.random() * shapes.length)];
      } while (shape1 === shape2);

      return [shape1, shape2, shape1, shape2, shape1];
    },
    // Triple repeat pattern
    () => {
      const shape1 = shapes[Math.floor(Math.random() * shapes.length)];
      let shape2;
      do {
        shape2 = shapes[Math.floor(Math.random() * shapes.length)];
      } while (shape1 === shape2);

      return [shape1, shape1, shape1, shape2, shape2];
    },
    // Growing pattern
    () => {
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      return [shape, shape + shape, shape + shape + shape, shape + shape + shape + shape, shape + shape + shape + shape + shape];
    }
  ];

  const selectedPatternFn = patternOptions[Math.floor(Math.random() * patternOptions.length)];
  const items = selectedPatternFn();

  // Choose a random position to be the missing item (but not the first position for clarity)
  const missingIndex = Math.floor(Math.random() * (items.length - 1)) + 1;
  const answer = items[missingIndex];

  // Generate options including the correct answer
  const options = [answer];

  // Add some incorrect but plausible options
  while (options.length < 4) {
    const incorrectOption = shapes[Math.floor(Math.random() * shapes.length)];
    if (!options.includes(incorrectOption)) {
      options.push(incorrectOption);
    }
  }

  // Shuffle options
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }

  return {
    type: 'shapes',
    items,
    missingIndex,
    options,
    answer
  };
};

const patternGenerators: PatternGeneratorFunction[] = [
  generateNumberPattern,
  generateShapePattern
];

const PatternPuzzle: React.FC = () => {
  const [pattern, setPattern] = useState<PatternType>(generateNumberPattern());
  const [selectedOption, setSelectedOption] = useState<string | number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState<number>(0);
  const { updateProgress } = useGameContext();

  const generateNewPattern = () => {
    const generator = patternGenerators[Math.floor(Math.random() * patternGenerators.length)];
    setPattern(generator());
  };

  const handleOptionClick = (option: string | number) => {
    if (selectedOption !== null) return; // Prevent selecting another option

    setSelectedOption(option);
    const correct = option === pattern.answer;
    setIsCorrect(correct);

    if (correct) {
      setScore(prevScore => prevScore + 1);
      playSound('success');
    } else {
      playSound('error');
    }

    // Save progress to context
    updateProgress('pattern-puzzle', score + (correct ? 1 : 0));

    // Move to next pattern after 2 seconds
    setTimeout(() => {
      setSelectedOption(null);
      setIsCorrect(null);
      generateNewPattern();
    }, 2000);
  };

  const playSound = (type: 'success' | 'error' | 'click') => {
    // In a real implementation, we would play actual sounds here
    console.log(`Playing ${type} sound`);
  };

  // Determine the colors for shape patterns
  const getPatternItemColor = (index: number) => {
    if (pattern.type === 'shapes') {
      const colors = ['#FF5722', '#2196F3', '#4CAF50', '#FFC107', '#9C27B0', '#3F51B5'];
      return colors[index % colors.length];
    }
    return undefined;
  };

  return (
    <Container>
      <PageTitle>Pattern Puzzle</PageTitle>

      <GameContainer>
        <FlexContainer justify="space-between">
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <ScoreDisplay>
            Score: <span>{score}</span>
          </ScoreDisplay>
        </FlexContainer>

        <InstructionText>
          Look at the pattern and select the missing piece!
        </InstructionText>

        <PatternContainer>
          {pattern.items.map((item, index) => (
            <PatternItem
              key={index}
              color={getPatternItemColor(index)}
              className={index === pattern.missingIndex ? 'empty' : ''}
            >
              {index !== pattern.missingIndex ? item : '?'}
            </PatternItem>
          ))}
        </PatternContainer>

        <OptionsContainer>
          {pattern.options.map((option, index) => (
            <PatternItem
              key={index}
              color={getPatternItemColor(index)}
              className={`
                selectable
                ${selectedOption === option ? 'selected' : ''}
                ${selectedOption === option && isCorrect ? 'correct' : ''}
                ${selectedOption === option && !isCorrect ? 'incorrect' : ''}
              `}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </PatternItem>
          ))}
        </OptionsContainer>

        {isCorrect !== null && (
          <FeedbackMessage isCorrect={isCorrect}>
            {isCorrect
              ? '✨ Great job! You found the pattern! ✨'
              : `Oops! The correct answer was ${pattern.answer}.`}
          </FeedbackMessage>
        )}
      </GameContainer>
    </Container>
  );
};

export default PatternPuzzle;
